import { useQuery, useReactiveVar } from '@apollo/client'
import { Close } from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme
} from '@mui/material'
import { ChangeEvent, useState } from 'react'
import { BucketType, UserProfileDocument } from '../../../../graphql/generated'
import { activeBucketIdVar, modalVar } from '../../../../providers/apollo/cache'
import logger from '../../../../utils/logger'
import useBucket from '../../hooks/useBucket'
import useBucketPlan from '../../hooks/useBucketPlan'
import useBucketUpdate from '../../hooks/useBucketUpdate'

export default function BucketUpgrade() {
  const theme = useTheme()
  const { mutation } = useBucketUpdate()
  const userProfile = useQuery(UserProfileDocument)
  const isSharingPlan = userProfile.data?.userProfile?.plan?.is_sharing_enabled
  const { bucket, currentUserIsOwner } = useBucket()
  const [bucketTitle, setBucketTitle] = useState<string | null>(null)
  const modal = useReactiveVar(modalVar)
  const handleClose = () => modalVar(null)
  const isGroupBucket = bucket?.bucketType === BucketType.Group
  const bucketId = useReactiveVar(activeBucketIdVar)
  const activeBucketId = bucketId ?? bucket?.id

  const { isSharingPlan: isSharingPlanBucket } = useBucketPlan()

  if (!activeBucketId) {
    logger.error('No Bucket Id')
    return (
      <Dialog data-test="invite-people-dialog" open={modal === 'bucketUpgrade'} onClose={handleClose}>
        <DialogTitle data-test="invite-users-title" sx={{ padding: { xs: '10px', sm: '20px 24px' } }}>
          Loading...
        </DialogTitle>
      </Dialog>
    )
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    setBucketTitle(inputValue)
  }

  const handleConvertBucket = () => {
    if (!bucketTitle) {
      return
    }

    mutation({
      variables: {
        bucketId: activeBucketId,
        data: { bucketType: BucketType.Group, title: bucketTitle }
      },
      onCompleted: () => {
        activeBucketIdVar(null)
        modalVar('bucketInvite')
      }
    })

    setBucketTitle(null)
  }

  const handleUpgradePlan = () => {
    window.location.href = '/settings#membership'
  }

  // If bucket is not owned by current user and not a sharing plan bucket
  if (!isSharingPlanBucket && !currentUserIsOwner) {
    return (
      <Dialog data-test="invite-people-dialog" open={modal === 'bucketUpgrade'} onClose={handleClose}>
        <DialogTitle
          data-test="invite-users-title"
          sx={{
            padding: { xs: '10px', sm: '20px 24px' },
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            fontWeight: 'bold'
          }}
        >
          Sharing Is Not Enabled
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: { xs: '7px', sm: '21px' },
            top: { xs: '7px', sm: '17px' },
            color: theme.palette.grey[500]
          }}
        >
          <Close />
        </IconButton>
        <DialogContent sx={{ padding: { xs: '0px 20px', sm: '0px 24px' } }}>
          <Typography>The owner of this bucket will need to upgrade to invite new members.</Typography>
        </DialogContent>
        <DialogActions sx={{ mb: 1, mx: 1, mt: { xs: 2, sm: 1 } }}>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  if (isSharingPlan) {
    return (
      <Dialog data-test="invite-people-dialog" open={modal === 'bucketUpgrade'} onClose={handleClose}>
        <DialogTitle
          data-test="invite-users-title"
          sx={{
            padding: { xs: '10px 20px', sm: '20px 24px' },
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            fontWeight: 'bold'
          }}
        >
          Give Your Bucket a Unique Name
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: { xs: '7px', sm: '21px' },
            top: { xs: '7px', sm: '17px' },
            color: theme.palette.grey[500]
          }}
        >
          <Close />
        </IconButton>
        <DialogContent sx={{ padding: { xs: '0px 20px', sm: '0px 24px' } }}>
          <Box>
            <DialogContentText
              id="conversion-dialog-text"
              sx={{
                color: 'black',
                marginBottom: '24px'
              }}
            >
              This is a good time to rename your bucket. Pick a unique name so your collaborators instantly know what it&apos;s for.
            </DialogContentText>

            <TextField
              name="rename-bucket-title-field-name"
              label="Bucket Name"
              fullWidth
              required
              autoComplete="off"
              id="rename-bucket-title"
              onChange={handleChange}
              value={bucketTitle}
              placeholder="Spring Soccer Season, Italy Trip 2025, The Jones Family Bucket"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ mb: 1, mx: 1, mt: { xs: 3, sm: 1 } }}>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConvertBucket}>
            {isGroupBucket ? 'Continue' : 'Rename and Invite'}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog data-test="invite-people-dialog" open={modal === 'bucketUpgrade'} onClose={handleClose}>
      <DialogTitle
        data-test="invite-users-title"
        sx={{
          padding: { xs: '10px', sm: '20px 24px' },
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          fontWeight: 'bold'
        }}
      >
        Invite People
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '21px' },
          top: { xs: '7px', sm: '17px' },
          color: theme.palette.grey[500]
        }}
      >
        <Close />
      </IconButton>
      <DialogContent sx={{ padding: { xs: '0px 20px', sm: '0px 24px' } }}>
        <Typography>You need to upgrade your bucket to invite new members.</Typography>
      </DialogContent>
      <DialogActions sx={{ mb: 1, mx: 1, mt: { xs: 2, sm: 1 } }}>
        <Button variant="text" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleUpgradePlan}>
          Upgrade Plan
        </Button>
      </DialogActions>
    </Dialog>
  )
}
